import clsx from 'clsx';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactGA from 'react-ga4';
import TagManager from 'react-gtm-module';
import iso3311a2 from 'iso-3166-1-alpha-2';
import * as queryString from 'query-string';
import { useRouter } from 'next/router';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  MenuItem,
  Divider,
  Box,
  TextField,
  Button,
  FormHelperText,
} from '@mui/material';
import { ChevronLeft, AddCircle } from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  IbanElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import AlertAlt from 'components/AlertAlt';
import ThemeButton from 'components/Button';
import StripeElement from 'components/StripeElement';
import PlanPaymentMethodCardBox from 'components/PlanPaymentMethodCardBox';
import {
  PlanEnum,
  PaymentMethodEnum,
  BillingIntervalEnum,
  CouponStatusEnum,
  GatewayEnum,
} from 'enums';
import PaymentService from 'services/PaymentService';
import { getEnv } from 'utils';
import { resolvePlan, getPlanData, resolveBillingInterval } from 'utils/plan';
import { embargoedCountries, europeanCountries, getCode, getCountries } from 'utils/countries';
import usePlan from 'hooks/usePlan';
import { useStyles } from './styles';
import WhatsappPhoneTextField from 'components/WhatsappPhoneTextField';
import ProfileService from 'services/ProfileService';
import getCleverTap from 'utils/clevertap';
import { useAuthUser } from 'features/auth/AuthUserContext';
import { StripeJapanProvider } from 'contexts/StripeJapanContext';

class PaymentError extends Error {
  constructor(message) {
    super(message);
    this.name = this.constructor.name;
  }
}

const Props = {
  coupon: PropTypes.shape({
    gateway: PropTypes.string.isRequired,
    context: PropTypes.string,
    code: PropTypes.string.isRequired,
    percent: PropTypes.number.isRequired,
    plan: PropTypes.oneOf(Object.values(PlanEnum)),
    billingInterval: PropTypes.oneOf(Object.values(BillingIntervalEnum)),
    status: PropTypes.oneOf(Object.values(CouponStatusEnum)).isRequired,
  }),
  onAllPlansButtonClick: PropTypes.func.isRequired,
  setEmbargoedCountryModalOpen: PropTypes.func.isRequired,
};

function Page(props) {
  const classes = useStyles();
  const router = useRouter();
  const stripe = useStripe();
  const stripeElements = useElements();
  const { user, isAuthenticated, profile } = useAuthUser();
  const [currentPlan] = usePlan();
  const [paymentMethod, setPaymentMethod] = useState(PaymentMethodEnum.CARD);
  const [cardNumberCompleted, setCardNumberCompleted] = useState(false);
  const [cardExpiryCompleted, setCardExpiryCompleted] = useState(false);
  const [cardCvcCompleted, setCardCvcCompleted] = useState(false);
  const [ibanCompleted, setIbanCompleted] = useState(false);
  const [name, setName] = useState('');
  const [address, setAddress] = useState({
    line1: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
  });
  const [email, setEmail] = useState('');
  const [plan, setPlan] = useState(PlanEnum.RESEARCHER);
  const [billingInterval, setBillingInterval] = useState(BillingIntervalEnum.YEAR);
  const [coupon, setCoupon] = useState();
  const [isBusy, setIsBusy] = useState(false);
  const [isEurope, setIsEurope] = useState(false);
  const [isChina, setIsChina] = useState(false);
  const [isEmbargoedCountry, setIsEmbargoedCountry] = useState(false);
  const [cardError, setCardError] = useState(null);
  const [hasUnknownError, setHasUnknownError] = useState(false);
  const [errorFields, setErrorFields] = useState([]);
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState('');
  const [paymentMethodList, setPaymentMethodList] = useState();
  const [showSepaPaymentOption, setShowSepaPaymentOption] = useState(false);
  const [showAlipayPaymentOption, setShowAlipayPaymentOption] = useState(false);
  const [showWeChatPayPaymentOption, setShowWeChatPayPaymentOption] = useState(false);
  const [showPaymentMethodForm, setShowPaymentMethodForm] = useState(false);
  const planData = getPlanData(plan, billingInterval, user.country_code);
  const paymentService = new PaymentService();
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const profileService = new ProfileService();
  const clevertap = getCleverTap();

  function calculateCouponTotalPrice() {
    if (coupon) {
      const discountAmount = planData.totalPrice * (coupon.percent / 100);

      return Math.round(planData.totalPrice - discountAmount);
    }

    return planData.totalPrice;
  }

  function getErrorFields() {
    const fields = [];

    if (paymentMethod === PaymentMethodEnum.CARD) {
      if (!cardNumberCompleted) {
        fields.push('card_number');
      }

      if (!cardExpiryCompleted) {
        fields.push('card_expiry');
      }

      if (!cardCvcCompleted) {
        fields.push('card_cvc');
      }

      if (!name) {
        fields.push('name');
      }

      if (!address.line1) {
        fields.push('line1');
      }

      if (!address.city) {
        fields.push('city');
      }

      if (!address.state) {
        fields.push('state');
      }

      if (!address.country) {
        fields.push('country');
      }
    }

    if (paymentMethod === PaymentMethodEnum.SEPA_DEBIT) {
      if (!ibanCompleted) {
        fields.push('iban');
      }

      if (!name) {
        fields.push('name');
      }

      if (!email) {
        fields.push('email');
      }

      if (!address.line1) {
        fields.push('line1');
      }

      if (!address.city) {
        fields.push('city');
      }

      if (!address.country) {
        fields.push('country');
      }
    }

    if (whatsappNumber && whatsappNumber.length <= 8) {
      fields.push('whatsappNumber');
    }

    return fields;
  }

  async function handleSaveWhatsappNumber() {
    await profileService.update(user.id, {
      whatsappNumber: whatsappNumber,
    });
  }

  function sendSubmitTrackingEvents() {
    ReactGA.event({
      category: 'checkout',
      action: `order_clicked_${planData.label}`,
      label: 'When the Complete Subscription button is clicked',
      value: user.id,
    });

    ReactGA.event({
      category: 'checkout',
      action: `order_clicked_${billingInterval}`,
      label: 'When the Complete Subscription button is clicked',
      value: user.id,
    });
  }

  function handleError(error) {
    let errorMessage = 'Could not process payment.';
    if (error instanceof PaymentError || error?.response?.status === 400) {
      errorMessage = error instanceof PaymentError ? error.message : error.response.data.message;

      setCardError(errorMessage);
    } else {
      setHasUnknownError(true);
    }

    const metadata = {
      email: user.email,
      user_id: user.id,
      error: error.message || error?.response?.data?.message,
      response: error.response || null,
    };

    // if (window.Intercom) {
    //   window.Intercom('trackEvent', errorMessage, metadata);
    // }

    paymentService.createPaymentInfo({
      gateway: paymentMethod === PaymentMethodEnum.CARD ? 'Stripe' : 'PayPal',
      total: planData.totalUsd,
      currency: 'USD',
      event: 'subscription_fail',
    });
  }

  async function handleCardPaymentMethod() {
    if (defaultPaymentMethodId) {
      return { id: defaultPaymentMethodId };
    }

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: stripeElements.getElement(CardNumberElement),
      billing_details: {
        name,
        address: {
          ...address,
          country: getCode(address.country),
        },
      },
    });

    if (error) {
      throw new PaymentError(error.message);
    }

    return paymentMethod;
  }

  async function handlePaymentThatRequiresCustomerAction(subscription, paymentMethod) {
    const paymentIntent = subscription.latest_invoice.payment_intent;
    const needsCardAuthentication =
      paymentIntent.status === 'requires_action' ||
      (subscription.paymentAttempts >= 1 && paymentIntent.status === 'requires_payment_method');

    if (needsCardAuthentication) {
      const { error, paymentIntent: newPaymentIntent } = await stripe.confirmCardPayment(
        paymentIntent.client_secret,
        {
          payment_method: paymentMethod.id,
          setup_future_usage: 'off_session',
        },
      );

      if (error) {
        throw new PaymentError(error.message);
      }

      if (newPaymentIntent.status === 'succeeded') {
        handleSuccededPayment();
      } else {
        throw new PaymentError('Card authentication failed. Try with another.');
      }
    }

    throw new Error('Unexpected error');
  }

  async function handleSetupIntent(id) {
    const { data: setupIntent } = await paymentService.getStripeSetupIntent(id);
    const { payment_method: paymentMethod } = setupIntent;

    const response = await paymentService.stripe({
      plan,
      billingInterval,
      paymentMethodId: paymentMethod,
      paymentMethodType: PaymentMethodEnum.ALIPAY,
      couponCode: coupon?.code,
    });

    if (response.data.status === 'active') {
      handleSuccededPayment();
    } else {
      console.log('subscription error', response.data);
    }
  }

  function makeAlipayPaymentUrl() {
    const url = `${getEnv('MTG_URL_API')}/payments/alipay/alipay_pc/${
      user.id
    }/${plan}/${billingInterval}?returnUrl=${makeSuccededPaymentRedirectUrl({ fullUrl: true })}`;
    return url;
  }

  function canSubmit() {
    const errors = getErrorFields();

    if (paymentMethod === PaymentMethodEnum.CARD && !defaultPaymentMethodId && errors.length) {
      setErrorFields(errors);

      return false;
    }

    if (paymentMethod === PaymentMethodEnum.SEPA_DEBIT && errors.length) {
      setErrorFields(errors);

      return false;
    }

    return true;
  }

  function makeSuccededPaymentRedirectUrl(
    options = { pendingConfirmation: false, fullUrl: false },
  ) {
    let url;

    if (options.pendingConfirmation) {
      url = `/my-account/overview?processingPayment=1&plan=${plan}`;
    } else {
      url = `/my-account/overview?succededPayment=1&plan=${plan}`;
    }

    if (coupon?.context) {
      url += `&couponContext=${coupon.context}`;
    }

    return options.fullUrl ? `${window.location.origin}/app${url}` : url;
  }

  function sendSuccededPaymentTrackingEvents(transactionId) {
    TagManager.dataLayer({
      dataLayer: {
        event: 'checkout',
        ecommerce: {
          purchase: {
            actionField: {
              id: transactionId || '',
              affiliation: 'Web',
              revenue: planData.totalUsd,
              tax: 0,
              shipping: 0,
            },
            products: [
              {
                name: planData.label,
                id: planData.id,
                price: planData.totalUsd,
                brand: 'Web',
                category: 'Subscription',
                variant: billingInterval,
                quantity: 1,
              },
            ],
          },
        },
      },
    });

    ReactGA.event({
      category: 'checkout',
      action: `payment_succeeded_${planData.label}`,
      label: 'When the subscription is complete',
      value: user.id,
    });

    ReactGA.event({
      category: 'checkout',
      action: `payment_succeeded_${billingInterval}`,
      label: 'When the subscription is complete',
      value: user.id,
    });

    clevertap.event.push('Purchase', {
      Product: 'MTG',
      Email: user.email,
      'Country code': user.country_code,
      URL: router.asPath,
      'Billing Interval': billingInterval,
      Plan: planData.label,
      Description: 'Payment Succeeded',
      Locale: 'checkout japan',
    });

    if (coupon?.context === 'black_november') {
      ReactGA.event({
        category: 'checkout',
        action: 'black_november_order_finished',
        label: 'when payment for the black november promotion is successfully completed',
        value: user.id,
      });

      clevertap.event.push('Purchase', {
        Product: 'MTG',
        Email: user.email,
        'Country code': user.country_code,
        URL: router.asPath,
        'Billing Interval': billingInterval,
        Plan: planData.label,
        Description: 'Black Friday Payment Succeeded',
        Locale: 'checkout japan',
        coupon: 'black_november',
      });
    }
  }

  function handleSuccededPayment(transactionId) {
    sendSuccededPaymentTrackingEvents(transactionId);

    router.push(makeSuccededPaymentRedirectUrl());
  }

  async function handleSubmit() {
    if (isEmbargoedCountry) {
      props.setEmbargoedCountryModalOpen(true);
      return;
    }

    if (!canSubmit()) {
      return;
    }

    if (whatsappNumber) {
      handleSaveWhatsappNumber();
    }

    sendSubmitTrackingEvents();
    setHasUnknownError(false);
    setCardError();
    setIsBusy(true);

    let transactionId;
    if (paymentMethod === PaymentMethodEnum.PAYPAL) {
      try {
        const response = await paymentService.paypal({
          plan,
          billingInterval,
          applicationContext: {
            returnUrl: makeSuccededPaymentRedirectUrl({ fullUrl: true }),
            cancelUrl: `${
              window.location.origin
            }/my-account/billing/checkout?failedPayment=1&${queryString.stringify({
              plan,
              billingInterval,
              ...(coupon && { couponCode: coupon.code }),
            })}`,
          },
        });

        transactionId = response.data.id;

        const redirectLink = response.data.links.find((link) => link.rel === 'approve');

        if (!redirectLink) {
          throw new Error('Could not find PayPal payment approval link');
        }

        window.location.href = redirectLink.href;
      } catch (e) {
        handleError(e);
      } finally {
        setIsBusy(false);
      }
    }

    if (paymentMethod === PaymentMethodEnum.CARD) {
      try {
        const paymentMethod = await handleCardPaymentMethod();
        const response = await paymentService.stripe({
          plan,
          billingInterval,
          paymentMethodId: paymentMethod.id,
          paymentMethodType: PaymentMethodEnum.CARD,
          couponCode: coupon?.code,
        });

        if (response.data.status === 'active') {
          transactionId = response.data.id;
          handleSuccededPayment(transactionId);
        } else {
          await handlePaymentThatRequiresCustomerAction(response.data, paymentMethod);
        }
      } catch (e) {
        handleError(e);
      } finally {
        setIsBusy(false);
      }
    }

    if (paymentMethod === PaymentMethodEnum.SEPA_DEBIT) {
      try {
        const { data: setupIntent } = await paymentService.createStripeSetupIntent({
          paymentMethod,
        });
        const clientSecret = setupIntent.client_secret;
        const iban = stripeElements.getElement(IbanElement);
        const result = await stripe.confirmSepaDebitSetup(clientSecret, {
          payment_method: {
            sepa_debit: iban,
            billing_details: {
              name,
              email,
              address: {
                ...address,
                country: getCode(address.country),
              },
            },
          },
        });

        if (result.error) {
          throw new PaymentError(result.error.message);
        }

        const paymentMethodId = result.setupIntent.payment_method;

        const response = await paymentService.stripe({
          plan,
          billingInterval,
          paymentMethodId,
          paymentMethodType: PaymentMethodEnum.SEPA_DEBIT,
          couponCode: coupon?.code,
        });

        if (response.data.status === 'active') {
          handleSuccededPayment({ pendingConfirmation: true });
        }
      } catch (e) {
        handleError(e);
      } finally {
        setIsBusy(false);
      }
    }

    if (paymentMethod === PaymentMethodEnum.ALIPAY) {
      router.push(makeAlipayPaymentUrl());
    }

    if (paymentMethod === PaymentMethodEnum.WECHAT_PAY) {
      try {
        const { data } = await paymentService.createOneTimeSubscription({
          plan,
          billingInterval,
          paymentMethod: PaymentMethodEnum.WECHAT_PAY,
        });
        const { error, paymentIntent } = await stripe.confirmWechatPayPayment(data.client_secret, {
          payment_method_options: {
            wechat_pay: {
              client: 'web',
            },
          },
        });

        if (error) {
          throw new PaymentError(error);
        }

        if (paymentIntent.status !== 'succeeded') {
          throw new PaymentError('Payment failed. Please try again.');
        }

        handleSuccededPayment();
      } catch (e) {
        console.log('ERROR', e);
        handleError(e);
      } finally {
        setIsBusy(false);
      }
    }
  }

  function handlePaymentMethodChange(event) {
    setPaymentMethod(event.target.value);
    setCardNumberCompleted(false);
    setCardExpiryCompleted(false);
    setCardCvcCompleted(false);
    setIbanCompleted(false);
    setErrorFields([]);
  }

  function handleSelectPaymentMethod(event) {
    setDefaultPaymentMethodId(event.target.value);
    attachDefaultPaymentMethod(event.target.value);
  }

  function handleAddCardPaymentMethod() {
    setShowPaymentMethodForm(true);
    setDefaultPaymentMethodId('');
  }

  async function attachDefaultPaymentMethod(id) {
    await paymentService.attachStripePaymentMethod({
      paymentMethodId: id,
    });
  }

  function handleFieldChange(field, value, stateHandler) {
    if (!!value && errorFields.includes(field)) {
      setErrorFields(errorFields.filter((errorField) => errorField !== field));
    }

    stateHandler(value);
  }

  useEffect(() => {
    async function fetchCheckoutData() {
      try {
        const { data } = await paymentService.data();

        setIsEurope(europeanCountries.includes(data.countryCode));
        setIsChina(data.countryCode === 'CN');
        setIsEmbargoedCountry(embargoedCountries.includes(data.countryCode));
      } catch (e) {
        console.error(e);
      }
    }

    fetchCheckoutData();
  }, []);

  useEffect(() => {
    setPlan(resolvePlan(router.query.plan));
    setBillingInterval(
      resolveBillingInterval(router.query.billingInterval, resolvePlan(router.query.plan)),
    );

    if (router.query.failedPayment === '1') {
      setHasUnknownError(true);
    }

    if (
      isAuthenticated &&
      router.query.redirect_status === 'succeeded' &&
      router.query.setup_intent &&
      router.query.setup_intent_client_secret
    ) {
      handleSetupIntent(router.query.setup_intent);
    }
  }, [router.query, isAuthenticated]);

  useEffect(() => {
    if (user.email && profile.name) {
      setName(profile.name);
      setEmail(user.email);
    }
  }, [profile]);

  useEffect(() => {
    setShowSepaPaymentOption(isEurope && (!currentPlan || currentPlan.currency === 'EUR'));

    setShowAlipayPaymentOption(isChina);

    setShowWeChatPayPaymentOption(isChina);
  }, [isEurope, isChina, currentPlan]);

  useEffect(() => {
    async function fetchStripePaymentMethods() {
      try {
        setPaymentMethodList(await paymentService.getStripePaymentMethods());
      } catch (e) {
        console.error(e);
      }
    }

    if (isAuthenticated) {
      fetchStripePaymentMethods();
    }
  }, [user]);

  useEffect(() => {
    if (paymentMethodList?.data.length > 0) {
      const firstPaymentMethod = paymentMethodList.data[0];
      const defaultPaymentMethod = paymentMethodList.data.find((item) => item.default);

      setDefaultPaymentMethodId(defaultPaymentMethod?.id ?? firstPaymentMethod?.id);
      setName(firstPaymentMethod?.billing_details?.name || '');
      setAddress({
        line1: firstPaymentMethod?.billing_details?.address?.line1 || '',
        city: firstPaymentMethod?.billing_details?.address?.city || '',
        state: firstPaymentMethod?.billing_details?.address?.state || '',
        postal_code: firstPaymentMethod?.billing_details?.address?.postal_code || '',
        country: firstPaymentMethod?.billing_details?.address?.country
          ? iso3311a2.getCountry(firstPaymentMethod.billing_details.address.country)
          : '',
      });
      setShowPaymentMethodForm(false);
    } else {
      setShowPaymentMethodForm(true);
    }
  }, [paymentMethodList]);

  useEffect(() => {
    if (props.coupon && props.coupon.status === CouponStatusEnum.AVAILABLE) {
      setCoupon(props.coupon);

      if (props.coupon.gateway === GatewayEnum.STRIPE) {
        setPaymentMethod(PaymentMethodEnum.CARD);
      }

      if (props.coupon.gateway === GatewayEnum.PAYPAL) {
        setPaymentMethod(PaymentMethodEnum.PAYPAL);
      }

      if (props.coupon.plan) {
        setPlan(props.coupon.plan);
      }

      if (props.coupon.billingInterval) {
        setBillingInterval(props.coupon.billingInterval);
      }
    }

    setCoupon(props.coupon);
  }, [props]);

  useEffect(() => {
    if (profile?.whatsappNumber) {
      setWhatsappNumber(profile.whatsappNumber);
    }
  }, [profile?.whatsappNumber]);

  return (
    <>
      <Button
        className={classes.allPlansButton}
        color="primary"
        startIcon={<ChevronLeft />}
        onClick={props.onAllPlansButtonClick}
      >
        すべてのプラン
      </Button>

      <Box mb={6} textAlign="center">
        <Typography variant="h5">
          <b>研究のインパクトを最大化させる</b>
        </Typography>
        <Typography>より精密なインフォグラフィックを作成する</Typography>
      </Box>

      <Grid container={true} spacing={3}>
        <Grid item={true} xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom={true}>
                <b>お申込みプラン</b>
              </Typography>
              <Typography>
                <b>{planData.label}</b>
              </Typography>

              {billingInterval === BillingIntervalEnum.YEAR && (
                <Typography className={classes.greyText}>
                  {planData.currency} {planData.monthlyPrice} / 月 ({planData.currency}{' '}
                  {planData.totalPrice} 年額プランの場合){' '}
                  {!coupon && (
                    <Chip
                      label={`${planData.discountPercent}% OFF`}
                      color="secondary"
                      size="small"
                      component="span"
                    />
                  )}
                </Typography>
              )}

              {billingInterval === BillingIntervalEnum.MONTH && !coupon && (
                <Typography className={classes.greyText}>
                  {planData.currency} {planData.monthlyPrice} / 月
                </Typography>
              )}

              {billingInterval === BillingIntervalEnum.MONTH && coupon && (
                <Typography className={classes.greyText}>
                  <del>
                    {planData.currency} {planData.monthlyPrice}
                  </del>{' '}
                  {planData.currency} {calculateCouponTotalPrice()} / 月{' '}
                  <Chip
                    label={`+${coupon.percent}% OFF`}
                    color="secondary"
                    size="small"
                    component="span"
                  />
                </Typography>
              )}

              {billingInterval === BillingIntervalEnum.SEMESTER && (
                <Typography className={classes.greyText}>
                  {planData.currency} {planData.monthlyPrice} / 月 ({planData.currency}{' '}
                  {planData.totalPrice} 半年ごと){' '}
                  <Chip
                    label={`${planData.discountPercent}% OFF`}
                    color="secondary"
                    size="small"
                    component="span"
                  />
                </Typography>
              )}

              {billingInterval === BillingIntervalEnum.QUARTER && !coupon && (
                <Typography className={classes.greyText}>
                  {planData.currency} {planData.monthlyPrice} / 月 ({planData.currency}{' '}
                  {planData.totalPrice} 四半期ごと){' '}
                  <Chip
                    label={`${planData.discountPercent}% OFF`}
                    color="secondary"
                    size="small"
                    component="span"
                  />
                </Typography>
              )}

              {billingInterval === BillingIntervalEnum.QUARTER && coupon && (
                <Typography className={classes.greyText}>
                  {planData.currency} {planData.monthlyPrice} / 月 ({planData.currency}{' '}
                  {planData.totalPrice} 四半期ごと){' '}
                  <Chip
                    label={`${planData.discountPercent}% OFF`}
                    color="secondary"
                    size="small"
                    component="span"
                  />
                </Typography>
              )}
            </CardContent>
          </Card>

          <Card className={classes.paymentDetailsCard}>
            <CardContent>
              <Typography variant="h6" gutterBottom={true}>
                <b>お支払い方法</b>
              </Typography>

              <FormControl variant="standard" className={classes.paymentMethodFormControl}>
                <RadioGroup
                  name="paymentMethod"
                  value={paymentMethod}
                  onChange={handlePaymentMethodChange}
                >
                  <FormControlLabel
                    className={clsx(classes.paymentOption, {
                      [classes.paymentOptionDisabled]:
                        coupon && coupon.gateway !== GatewayEnum.STRIPE,
                    })}
                    label={
                      <>
                        <img src="/images/visa-logo.svg" alt="Visa Logo" />
                        <img src="/images/master-card-logo.svg" alt="Master Card Logo" />
                        <img src="/images/american-express-logo.svg" alt="American Express Logo" />
                        <img src="/images/discover-logo.svg" alt="Discover Logo" />
                      </>
                    }
                    disabled={coupon && coupon.gateway !== GatewayEnum.STRIPE}
                    value={PaymentMethodEnum.CARD}
                    control={<Radio color="secondary" />}
                  />

                  <FormControlLabel
                    className={clsx(classes.paymentOption, {
                      [classes.paymentOptionDisabled]:
                        (coupon && coupon.gateway !== GatewayEnum.PAYPAL) ||
                        [BillingIntervalEnum.SEMESTER, BillingIntervalEnum.QUARTER].includes(
                          billingInterval,
                        ),
                    })}
                    label={<img src="/images/paypal-logo.svg" alt="PayPal Logo" />}
                    disabled={
                      (coupon && coupon.gateway !== GatewayEnum.PAYPAL) ||
                      [BillingIntervalEnum.SEMESTER, BillingIntervalEnum.QUARTER].includes(
                        billingInterval,
                      )
                    }
                    value={PaymentMethodEnum.PAYPAL}
                    control={<Radio color="secondary" />}
                  />

                  {showSepaPaymentOption && (
                    <FormControlLabel
                      className={clsx(classes.paymentOption, {
                        [classes.paymentOptionDisabled]:
                          coupon && coupon.gateway !== GatewayEnum.STRIPE,
                      })}
                      label={<img src="/images/sepa-debit-logo.svg" alt="SEPA Debit Logo" />}
                      disabled={coupon && coupon.gateway !== GatewayEnum.STRIPE}
                      value={PaymentMethodEnum.SEPA_DEBIT}
                      control={<Radio color="secondary" />}
                    />
                  )}

                  {showAlipayPaymentOption && (
                    <FormControlLabel
                      className={clsx(classes.paymentOption, {
                        [classes.paymentOptionDisabled]:
                          coupon && coupon.gateway !== GatewayEnum.STRIPE,
                      })}
                      label={<img src="/images/alipay-logo.png" alt="Alipay Logo" />}
                      disabled={coupon && coupon.gateway !== GatewayEnum.STRIPE}
                      value={PaymentMethodEnum.ALIPAY}
                      control={<Radio color="secondary" />}
                    />
                  )}

                  {showWeChatPayPaymentOption && (
                    <FormControlLabel
                      className={clsx(classes.paymentOption, {
                        [classes.paymentOptionDisabled]:
                          coupon && coupon.gateway !== GatewayEnum.STRIPE,
                      })}
                      label={
                        <img
                          src="/images/wechat-pay-logo.svg"
                          alt="WeChat Pay Logo"
                          style={{ width: 85 }}
                        />
                      }
                      disabled={coupon && coupon.gateway !== GatewayEnum.STRIPE}
                      value={PaymentMethodEnum.WECHAT_PAY}
                      control={<Radio color="secondary" />}
                    />
                  )}
                </RadioGroup>
              </FormControl>

              {paymentMethod === PaymentMethodEnum.CARD &&
                paymentMethodList &&
                !showPaymentMethodForm && (
                  <div>
                    <Box mt={2} mb={4}>
                      <Divider />
                    </Box>

                    <FormControl variant="standard">
                      <RadioGroup
                        name="paymentMethod"
                        value={defaultPaymentMethodId}
                        onChange={handleSelectPaymentMethod}
                      >
                        <Grid container={true} spacing={2}>
                          {paymentMethodList &&
                            paymentMethodList.data.map((item, index) => (
                              <Grid item={true} xs={12} sm={12} key={index}>
                                <FormControlLabel
                                  label={
                                    <PlanPaymentMethodCardBox
                                      item={item}
                                      isDefault={item.id === defaultPaymentMethodId}
                                      isExpired={item.expired}
                                    />
                                  }
                                  value={item.id}
                                  control={<Radio disabled={item.expired} />}
                                />
                              </Grid>
                            ))}
                        </Grid>
                      </RadioGroup>
                    </FormControl>

                    <div>
                      <Button
                        className={classes.addCardButton}
                        color="primary"
                        startIcon={<AddCircle />}
                        onClick={handleAddCardPaymentMethod}
                      >
                        新しいカードを追加する
                      </Button>
                    </div>
                  </div>
                )}

              {paymentMethod === PaymentMethodEnum.CARD &&
                (!paymentMethodList || showPaymentMethodForm) && (
                  <>
                    <Box mb={2}>
                      <Typography variant="body2" color="secondary" gutterBottom={true}>
                        *は必須項目です。
                      </Typography>
                    </Box>

                    <Grid container={true} spacing={1}>
                      <Grid item={true} xs={12} md={8}>
                        <div className={classes.inputCardNumber}>
                          <span>カード番号</span>
                          <span className="highlight">*</span>
                          <StripeElement
                            element={CardNumberElement}
                            error={errorFields.includes('card_number')}
                            onChange={(event) =>
                              handleFieldChange(
                                'card_number',
                                event.complete,
                                setCardNumberCompleted,
                              )
                            }
                          />
                        </div>

                        {errorFields.includes('card_number') && (
                          <FormHelperText variant="outlined" error={true}>
                            このフィールドに正しく入力してください
                          </FormHelperText>
                        )}
                      </Grid>

                      <Grid item={true} xs={12} md={2}>
                        <div className={classes.inputCardNumber}>
                          <span>有効期限</span>
                          <span className="highlightDate">*</span>
                          <StripeElement
                            element={CardExpiryElement}
                            error={errorFields.includes('card_expiry')}
                            onChange={(event) =>
                              handleFieldChange(
                                'card_expiry',
                                event.complete,
                                setCardExpiryCompleted,
                              )
                            }
                          />
                        </div>

                        {errorFields.includes('card_expiry') && (
                          <FormHelperText variant="outlined" error={true}>
                            このフィールドに正しく入力してください
                          </FormHelperText>
                        )}
                      </Grid>

                      <Grid item={true} xs={12} md={2}>
                        <div className={classes.inputCardNumber}>
                          <span>CVC</span>
                          <span className="highlightCVC">*</span>
                          <StripeElement
                            element={CardCvcElement}
                            error={errorFields.includes('card_cvc')}
                            onChange={(event) =>
                              handleFieldChange('card_cvc', event.complete, setCardCvcCompleted)
                            }
                          />
                        </div>

                        {errorFields.includes('card_cvc') && (
                          <FormHelperText variant="outlined" error={true}>
                            このフィールドに正しく入力してください
                          </FormHelperText>
                        )}
                      </Grid>

                      <Grid item={true} xs={12} sm={12}>
                        <FormControl variant="standard" fullWidth={true} margin="dense">
                          <TextField
                            variant="outlined"
                            error={errorFields.includes('name')}
                            helperText={
                              errorFields.includes('name')
                                ? 'このフィールドに正しく入力してください'
                                : undefined
                            }
                            label={
                              <>
                                名称<span className={classes.colorMandatory}>*</span>
                              </>
                            }
                            placeholder=""
                            InputLabelProps={{
                              shrink: true,
                            }}
                            name="name"
                            value={name}
                            onChange={(event) =>
                              handleFieldChange('name', event.target.value, setName)
                            }
                          />
                        </FormControl>
                      </Grid>

                      <Grid item={true} xs={12}>
                        <FormControl variant="standard" fullWidth={true} margin="dense">
                          <TextField
                            variant="outlined"
                            error={errorFields.includes('line1')}
                            helperText={
                              errorFields.includes('line1')
                                ? 'このフィールドに正しく入力してください'
                                : undefined
                            }
                            label={
                              <>
                                住所<span className={classes.colorMandatory}>*</span>
                              </>
                            }
                            placeholder=""
                            InputLabelProps={{
                              shrink: true,
                            }}
                            name="line1"
                            value={address.line1}
                            onChange={(event) =>
                              handleFieldChange(
                                'line1',
                                { ...address, line1: event.target.value },
                                setAddress,
                              )
                            }
                          />
                        </FormControl>
                      </Grid>

                      <Grid item={true} xs={6} sm={4}>
                        <FormControl variant="standard" fullWidth={true} margin="dense">
                          <TextField
                            variant="outlined"
                            error={errorFields.includes('city')}
                            helperText={
                              errorFields.includes('city')
                                ? 'このフィールドに正しく入力してください'
                                : undefined
                            }
                            label={
                              <>
                                都市<span className={classes.colorMandatory}>*</span>
                              </>
                            }
                            placeholder=""
                            InputLabelProps={{
                              shrink: true,
                            }}
                            name="city"
                            value={address.city}
                            onChange={(event) =>
                              handleFieldChange(
                                'city',
                                { ...address, city: event.target.value },
                                setAddress,
                              )
                            }
                          />
                        </FormControl>
                      </Grid>

                      <Grid item={true} xs={6} sm={2}>
                        <FormControl variant="standard" fullWidth={true} margin="dense">
                          <TextField
                            variant="outlined"
                            error={errorFields.includes('state')}
                            helperText={
                              errorFields.includes('state')
                                ? 'このフィールドに正しく入力してください'
                                : undefined
                            }
                            label={
                              <>
                                都道府県<span className={classes.colorMandatory}>*</span>
                              </>
                            }
                            placeholder=""
                            InputLabelProps={{
                              shrink: true,
                            }}
                            name="state"
                            value={address.state}
                            onChange={(event) =>
                              handleFieldChange(
                                'state',
                                { ...address, state: event.target.value },
                                setAddress,
                              )
                            }
                          />
                        </FormControl>
                      </Grid>

                      <Grid item={true} xs={6} sm={3}>
                        <FormControl variant="standard" fullWidth={true} margin="dense">
                          <TextField
                            variant="outlined"
                            error={errorFields.includes('postal_code')}
                            helperText={
                              errorFields.includes('postal_code')
                                ? 'このフィールドに正しく入力してください'
                                : undefined
                            }
                            label="郵便番号"
                            placeholder=""
                            InputLabelProps={{
                              shrink: true,
                            }}
                            name="postal_code"
                            value={address.postal_code}
                            onChange={(event) =>
                              handleFieldChange(
                                'postal_code',
                                { ...address, postal_code: event.target.value },
                                setAddress,
                              )
                            }
                          />
                        </FormControl>
                      </Grid>

                      <Grid item={true} xs={6} sm={3}>
                        <FormControl variant="standard" fullWidth={true} margin="dense">
                          <Autocomplete
                            options={['', ...getCountries()]}
                            value={
                              address.country === 'Taiwan, Province of China'
                                ? 'Taiwan'
                                : address.country
                            }
                            onChange={(event, value) =>
                              handleFieldChange(
                                'country',
                                { ...address, country: value || '' },
                                setAddress,
                              )
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                error={errorFields.includes('country')}
                                helperText={
                                  errorFields.includes('country')
                                    ? 'このフィールドに正しく入力してください'
                                    : undefined
                                }
                                label={
                                  <>
                                    国名<span className={classes.colorMandatory}>*</span>
                                  </>
                                }
                                placeholder=""
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                name="country"
                              />
                            )}
                          />
                        </FormControl>
                      </Grid>

                      <Box pt={2} ml={0.5} width="100%">
                        <WhatsappPhoneTextField
                          variant="outlined"
                          label="Whatsapp番号"
                          onChange={setWhatsappNumber}
                          value={whatsappNumber}
                          error={errorFields.includes('whatsappNumber')}
                          helperText={
                            errorFields.includes('whatsappNumber')
                              ? 'このフィールドに正しく入力してください'
                              : undefined
                          }
                        />
                      </Box>
                    </Grid>
                  </>
                )}

              {[PaymentMethodEnum.ALIPAY, PaymentMethodEnum.WECHAT_PAY].includes(paymentMethod) && (
                <Box mb={2}>
                  <Typography className={classes.greyText}>
                    {paymentMethod === PaymentMethodEnum.ALIPAY && 'Alipay'}
                    {paymentMethod === PaymentMethodEnum.WECHAT_PAY && 'WeChat Pay'}
                    は1回限りのご利用となり、自動更新はございません。
                    サブスクリプションの終了後も引き続きご利用になりたい場合は、このページに戻り、新たに手動でお支払いいただく必要があります。
                  </Typography>
                </Box>
              )}

              {[
                PaymentMethodEnum.PAYPAL,
                PaymentMethodEnum.ALIPAY,
                PaymentMethodEnum.WECHAT_PAY,
              ].includes(paymentMethod) && (
                <Typography className={classes.greyText}>
                  で購入する場合は、「購入を完了する」をクリックしてください。{' '}
                  {paymentMethod === PaymentMethodEnum.PAYPAL && 'PayPal'}
                  {paymentMethod === PaymentMethodEnum.ALIPAY && 'Alipay'}
                  {paymentMethod === PaymentMethodEnum.WECHAT_PAY && 'WeChat Pay'}.
                  お支払いを完了するには、別のページにリダイレクトされます。
                </Typography>
              )}

              {paymentMethod === PaymentMethodEnum.SEPA_DEBIT && (
                <>
                  <Grid container={true} spacing={1} className={classes.paymentMethodSepaForm}>
                    <Grid item={true} xs={12} sm={12}>
                      <Typography className={clsx(classes.greyText, classes.paymentMethodSepaText)}>
                        SEPA口座振替は再利用可能な遅延通知型支払方法です。{' '}
                        つまり、お支払いが確認され、購読が有効になるまで平均5営業日かかります。つまり、お支払いが確認され、購読が有効になるまでに平均5営業日かかるということです。確認期限は14営業日）。
                      </Typography>

                      <StripeElement
                        element={IbanElement}
                        error={errorFields.includes('iban')}
                        options={{ supportedCountries: ['SEPA'] }}
                        onChange={(event) =>
                          handleFieldChange('iban', event.complete, setIbanCompleted)
                        }
                      />

                      {errorFields.includes('iban') && (
                        <FormHelperText variant="outlined" error={true}>
                          このフィールドに正しく入力してください
                        </FormHelperText>
                      )}

                      <FormControl variant="standard" fullWidth={true} margin="dense">
                        <TextField
                          variant="outlined"
                          error={errorFields.includes('name')}
                          helperText={
                            errorFields.includes('name')
                              ? 'このフィールドに正しく入力してください'
                              : null
                          }
                          label="名称"
                          name="name"
                          value={name}
                          onChange={(event) =>
                            handleFieldChange('name', event.target.value, setName)
                          }
                        />
                      </FormControl>

                      <FormControl variant="standard" fullWidth={true} margin="dense">
                        <TextField
                          variant="outlined"
                          error={errorFields.includes('email')}
                          helperText={
                            errorFields.includes('email')
                              ? 'このフィールドに正しく入力してください'
                              : undefined
                          }
                          label="電子メール"
                          name="email"
                          type="email"
                          value={email}
                          onChange={(event) =>
                            handleFieldChange('email', event.target.value, setEmail)
                          }
                        />
                      </FormControl>
                    </Grid>

                    <Grid item={true} xs={12}>
                      <FormControl variant="standard" fullWidth={true} margin="dense">
                        <TextField
                          variant="outlined"
                          error={errorFields.includes('line1')}
                          helperText={
                            errorFields.includes('line1')
                              ? 'このフィールドに正しく入力してください'
                              : undefined
                          }
                          label="住所"
                          name="line1"
                          value={address.line1}
                          onChange={(event) =>
                            handleFieldChange(
                              'line1',
                              { ...address, line1: event.target.value },
                              setAddress,
                            )
                          }
                        />
                      </FormControl>
                    </Grid>

                    <Grid item={true} xs={6} sm={6}>
                      <FormControl variant="standard" fullWidth={true} margin="dense">
                        <TextField
                          variant="outlined"
                          error={errorFields.includes('city')}
                          helperText={
                            errorFields.includes('city')
                              ? 'このフィールドに正しく入力してください'
                              : undefined
                          }
                          label="都市"
                          name="city"
                          value={address.city}
                          onChange={(event) =>
                            handleFieldChange(
                              'city',
                              { ...address, city: event.target.value },
                              setAddress,
                            )
                          }
                        />
                      </FormControl>
                    </Grid>

                    <Grid item={true} xs={6} sm={3}>
                      <FormControl variant="standard" fullWidth={true} margin="dense">
                        <TextField
                          variant="outlined"
                          error={errorFields.includes('postal_code')}
                          helperText={
                            errorFields.includes('postal_code')
                              ? 'このフィールドに正しく入力してください'
                              : undefined
                          }
                          label="郵便番号"
                          name="postal_code"
                          value={address.postal_code}
                          onChange={(event) =>
                            handleFieldChange(
                              'postal_code',
                              { ...address, postal_code: event.target.value },
                              setAddress,
                            )
                          }
                        />
                      </FormControl>
                    </Grid>

                    <Grid item={true} xs={6} sm={3}>
                      <FormControl variant="standard" fullWidth={true} margin="dense">
                        <Autocomplete
                          options={['', ...getCountries()]}
                          value={
                            address.country === 'Taiwan, Province of China'
                              ? 'Taiwan'
                              : address.country
                          }
                          onChange={(event, value) =>
                            handleFieldChange(
                              'country',
                              { ...address, country: value || '' },
                              setAddress,
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              error={errorFields.includes('country')}
                              helperText={
                                errorFields.includes('country')
                                  ? 'このフィールドに正しく入力してください'
                                  : undefined
                              }
                              label="国名"
                              name="country"
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>

                  <Typography className={classes.greyText}>
                    支払い情報を提供し、この支払いを確認することにより、お客様は、(A) Mind The
                    Graph,
                    Inc.および当社の支払いサービスプロバイダーであるStripeが、お客様の口座から引き落とすための指示をお客様の銀行に送信すること、および(B)
                    お客様の銀行がその指示に従ってお客様の口座から引き落とすことを承認したものとみなされます。お客様の権利の一部として
                    あなたの銀行との契約条件に基づき、あなたの銀行から払い戻しを受ける権利があります。払い戻しを受ける権利があります。払い戻しは、以下の日付から8週間以内に請求する必要があります。口座から引き落とされた日から8週間以内にご請求ください。あなたの権利については
                    明細書に記載されています。あなたは以下の通知を受け取ることに同意します。引き落としが行われる2日前までに通知を受け取ることに同意するものとします。
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item={true} xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom={true}>
                <b>お支払い</b>
              </Typography>

              <TextField
                fullWidth={true}
                select={true}
                variant="outlined"
                disabled={
                  !!coupon?.billingInterval || billingInterval === BillingIntervalEnum.SEMESTER
                }
                value={billingInterval}
                onChange={(event) => setBillingInterval(event.target.value)}
              >
                <MenuItem value={BillingIntervalEnum.YEAR}>年額プラン</MenuItem>

                <MenuItem value={BillingIntervalEnum.MONTH}>月額プラン</MenuItem>

                {plan === PlanEnum.RESEARCHER &&
                  billingInterval === BillingIntervalEnum.SEMESTER && (
                    <MenuItem value={BillingIntervalEnum.SEMESTER}>学期プラン</MenuItem>
                  )}

                {[PlanEnum.STUDENT, PlanEnum.RESEARCHER].includes(plan) &&
                  billingInterval === BillingIntervalEnum.QUARTER && (
                    <MenuItem value={BillingIntervalEnum.QUARTER}>四半期プラン</MenuItem>
                  )}
              </TextField>

              <Box my={2}>
                {billingInterval === BillingIntervalEnum.YEAR && (
                  <AlertAlt icon={false}>
                    年額プランで{planData.discountPercent}%節約できます。
                  </AlertAlt>
                )}

                {billingInterval === BillingIntervalEnum.MONTH && (
                  <AlertAlt icon={false} severity="warning">
                    年間契約に切り替えるとお得{' '}
                    {
                      getPlanData(plan, BillingIntervalEnum.YEAR, paymentMethod, user.country_code)
                        .currency
                    }{' '}
                    {
                      getPlanData(plan, BillingIntervalEnum.YEAR, paymentMethod, user.country_code)
                        .discount
                    }
                    /年
                  </AlertAlt>
                )}
              </Box>

              <Divider />

              {!coupon ? (
                <Box display="flex" justifyContent="space-between" my={2}>
                  <Typography variant="h6">
                    <b>合計</b>
                  </Typography>

                  <Typography variant="h6">
                    <b>
                      {planData.currency} {planData.totalPrice}
                    </b>
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box my={2}>
                    <TextField
                      fullWidth={true}
                      variant="outlined"
                      label="クーポン"
                      name="coupon"
                      disabled={true}
                      value={coupon.code}
                    />
                  </Box>

                  <Divider />

                  <Box display="flex" justifyContent="space-between" my={2}>
                    <Typography variant="h6">
                      <b>合計</b>
                    </Typography>

                    <Typography variant="h6">
                      <b>
                        <del>
                          {planData.currency} {planData.totalPrice}
                        </del>
                      </b>
                    </Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography>
                      <b>クーポン</b>{' '}
                      <Chip
                        label={`+${coupon.percent}% OFF`}
                        color="secondary"
                        size="small"
                        component="span"
                      />
                    </Typography>

                    <Typography variant="h6">
                      <b>
                        {planData.currency} {calculateCouponTotalPrice()}
                      </b>
                    </Typography>
                  </Box>
                </>
              )}

              <div className="button-checkout-test">
                <ThemeButton variant="primary" isLoading={isBusy} onClick={handleSubmit}>
                  購入する
                </ThemeButton>
              </div>
            </CardContent>
          </Card>

          <Typography className={classes.termsText} variant="body2" align="center">
            お支払いを続行することで、{' '}
            <a href="https://mindthegraph.com/blog/faq/terms-of-use/" target="_blank">
              <b>利用規約とプライバシーポリシー</b>
            </a>
            に同意したとみなされます。 サブスクリプションは利用期間終了時に自動的に更新されます。
            また、サブスクリプションはいつでも中止できます。
          </Typography>

          {(cardError || hasUnknownError) && (
            <Box mb={3} mt={1.5}>
              <AlertAlt severity="error">
                {cardError ||
                  'お支払いを処理できませんでした。選択した支払いオプションで再試行するか、その他を選択してください。'}
              </AlertAlt>
            </Box>
          )}
        </Grid>
      </Grid>
    </>
  );
}

export default function PageJapanWrapper(props) {
  return (
    <StripeJapanProvider>
      <Page {...props} />
    </StripeJapanProvider>
  );
}

Page.propTypes = Props;
